import React from 'react';
import moment from 'moment';
import { FormGroup, Input, Alert, Button } from 'reactstrap';
import Page from '../../containers/page';
import RouteComponent from '../../helpers/route';
import { getSearchParams } from '../../helpers/link';
import ExternalApiOverview from '../../components/externalApiOverview';
import ExternalApiAnalytics from '../../components/externalApiAnalytics';
import ExternalApiInsights from '../../components/externalApiInsights';
import ExternalApiLogs from '../../components/externalApiLogs';
import ClientAutocompleteInput from '../../components/clientAutocompleteInput';
import { MAX_RANGE_DAYS } from '../../helpers/externalApiStats';
import {
    PAGE_PERIODS, MAX_OVERVIEW_DAYS, CLIENT_SERIES_COUNT,
    detailRange, isDetailRangeTooLong, overviewRange, clientAnalyticsUrl, createClientColorRegistry,
} from '../../helpers/externalApiOverview';

/**
* Global "API analytics" page (VD-725): the whole external API at once - all
* clients, total load and who makes it. One period picker and one set of
* filters on top feed four tabs:
*   - Overview (`/overview`, daily counters, up to a year)
*   - Load (`/stats` + `clientSeries`, up to 31 days)
*   - Analysis (`/insights`, up to 31 days)
*   - Logs (`/logs`, up to 31 days)
* Everything lives in the URL so a view can be shared or reloaded.
*/

const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'load', label: 'Load' },
    { key: 'analysis', label: 'Analysis' },
    { key: 'logs', label: 'Logs' },
];

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const SCOPE_KEYS = ['clientId', 'clientLabel', 'apiKeyId', 'apiKeyLabel', 'route', 'method', 'replica'];
// `/insights` has no replica filter
const REPLICA_TABS = ['load', 'logs'];

const styles = {
    filterBar: {
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14,
        backgroundColor: '#FFFFFF', borderRadius: 6, padding: '16px 18px',
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 14,
    },
    filterGroup: { marginBottom: 0, minWidth: 140 },
    filterLabel: { fontSize: 11, fontWeight: 600, color: '#818FA5', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
    chip: {
        display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
        borderRadius: 16, backgroundColor: '#E6EDF5', fontSize: 13, color: '#3A3F56',
    },
    chipClose: { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: '#818FA5', lineHeight: 1 },
    link: { background: 'transparent', border: 'none', padding: 0, color: '#33405E', textDecoration: 'underline', cursor: 'pointer' },
};

class Screen extends RouteComponent {
    constructor(props) {
        super(props);

        // a link from "API analytics (all requests)": one request ID, a window around its time
        // (this.state doesn't exist yet, so read the URL directly instead of _getParams)
        let params = getSearchParams(props[0].location.search, {});
        let linkedRange = params.logsFrom && params.logsTo ? { from: params.logsFrom, to: params.logsTo } : null;
        let linkedFilters = params.logsRequestId ? { requestId: params.logsRequestId } : null;

        this.state = {
            ...this.state,
            routeDraft: null,
            // a link to one minute/window ("open in logs") narrows only the Logs tab,
            // the page period stays as it was for the other tabs
            logsRangeOverride: linkedRange,
            logsFilters: linkedFilters,
            logsFilterToken: 0,
            // replica slots seen on the Load tab, per period, for the replica dropdown
            replicaSlots: null,
        };

        // same client = same color on every stacked chart of the page
        this.colorFor = createClientColorRegistry();
        this._rangeCache = {};
    }

    _period = () => {
        let params = this._getParams();
        return {
            preset: params.preset || '7d',
            customFrom: params.customFrom || '',
            customTo: params.customTo || '',
        };
    }

    _scope = () => {
        let params = this._getParams();
        let scope = {};
        SCOPE_KEYS.forEach((key) => { scope[key] = params[key] || ''; });
        return scope;
    }

    // presets are relative to "now", so cache them per period - otherwise every
    // render would produce a new range and every tab would refetch forever
    _cached = (name, period, compute) => {
        let key = JSON.stringify(period);
        if (!this._rangeCache[name] || this._rangeCache[name].key !== key) {
            this._rangeCache[name] = { key, value: compute(period) };
        }
        return this._rangeCache[name].value;
    }

    _setParams = (patch) => {
        this.updateMultipleParams(Object.keys(patch).map((name) => ({ name, value: patch[name] })), false);
    }

    _setPeriod = (patch) => {
        this.setState({ logsRangeOverride: null });
        this._setParams(patch);
    }

    _setScope = (patch) => {
        let clean = {};
        Object.keys(patch).forEach((key) => { clean[key] = patch[key] || ''; });
        this._setParams(clean);
    }

    _onReplicasLoaded = (slots) => {
        let key = JSON.stringify(this._period());
        this.setState((prev) => {
            let known = prev.replicaSlots && prev.replicaSlots.key === key ? prev.replicaSlots.slots : [];
            let merged = Array.from(new Set([...known, ...slots.map(String)])).sort((a, b) => Number(a) - Number(b));
            return merged.join() === known.join() ? null : { replicaSlots: { key, slots: merged } };
        });
    }

    _onRouteDraft = (value) => {
        this.setState({ routeDraft: value });
        if (this._routeTimer) clearTimeout(this._routeTimer);
        this._routeTimer = setTimeout(() => {
            this.setState({ routeDraft: null });
            this._setScope({ route: value });
        }, 300);
    }

    componentDidMount() {
        // the link's one-off params are now in state, keep the URL clean
        let params = this._getParams();
        if (params.logsRequestId || params.logsFrom || params.logsTo) {
            this._setParams({ logsRequestId: '', logsFrom: '', logsTo: '' });
        }
    }

    componentWillUnmount() {
        if (this._routeTimer) clearTimeout(this._routeTimer);
    }

    _onOpenClient = (row) => {
        if (!row || !row.clientId) return;
        this.props[0].history.push(clientAnalyticsUrl(row, this._period()));
    }

    _onOpenLogs = (filters) => {
        let { from, to, ...rest } = filters || {};
        let scopePatch = {};
        let logsFilters = {};

        Object.keys(rest).forEach((key) => {
            if (rest[key] === undefined || rest[key] === null || rest[key] === '') return;
            if (SCOPE_KEYS.indexOf(key) !== -1) scopePatch[key] = rest[key];
            else logsFilters[key] = rest[key];
        });

        // the Insights panels always pass the current period along; only a
        // narrower window (a minute, a burst) becomes a Logs-only override
        let current = this._cached('detail', this._period(), detailRange);
        let override = from && to && (!current || from !== current.from || to !== current.to) ? { from, to } : null;

        this.setState((prev) => ({
            logsRangeOverride: override,
            logsFilters,
            logsFilterToken: prev.logsFilterToken + 1,
        }));

        this._setParams({ tab: 'logs', ...scopePatch });
    }

    _renderPeriodPicker = () => {
        let lang = this.props.lang;
        let period = this._period();

        return (
            <>
                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Period'.translate(lang)}</div>
                    <Input type="select" style={{ height: 42 }} value={period.preset} onChange={(e) => this._setPeriod({ preset: e.target.value })}>
                        {PAGE_PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label.translate(lang)}</option>)}
                    </Input>
                </FormGroup>

                {period.preset === 'custom' ? (
                    <>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'From'.translate(lang)}</div>
                            <Input type="datetime-local" style={{ height: 42 }} value={period.customFrom} onChange={(e) => this._setPeriod({ customFrom: e.target.value })} />
                        </FormGroup>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'To'.translate(lang)}</div>
                            <Input type="datetime-local" style={{ height: 42 }} value={period.customTo} onChange={(e) => this._setPeriod({ customTo: e.target.value })} />
                        </FormGroup>
                    </>
                ) : null}
            </>
        );
    }

    // a dropdown once the Load tab has seen this period's replicas, a plain number before that
    _renderReplicaFilter = (scope) => {
        let lang = this.props.lang;
        let known = this.state.replicaSlots && this.state.replicaSlots.key === JSON.stringify(this._period()) ? this.state.replicaSlots.slots : [];
        let slots = Array.from(new Set([...known, ...(scope.replica ? [String(scope.replica)] : [])]));

        return (
            <FormGroup style={styles.filterGroup}>
                <div style={styles.filterLabel}>{'Replica'.translate(lang)}</div>
                {known.length ? (
                    <Input type="select" style={{ height: 42 }} value={scope.replica} onChange={(e) => this._setScope({ replica: e.target.value })}>
                        <option value="">{'All'.translate(lang)}</option>
                        {slots.map((r) => <option key={r} value={r}>{r}</option>)}
                    </Input>
                ) : (
                    <Input type="number" min={1} style={{ height: 42, maxWidth: 110 }} placeholder={'All'.translate(lang)} value={scope.replica} onChange={(e) => this._setScope({ replica: e.target.value })} />
                )}
            </FormGroup>
        );
    }

    _renderScopeFilters = (tab) => {
        let lang = this.props.lang;
        let scope = this._scope();
        let routeValue = this.state.routeDraft !== null ? this.state.routeDraft : scope.route;

        // Overview always covers every client (the daily counters have no per-route/key split)
        if (tab === 'overview') {
            return null;
        }

        return (
            <>
                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Client'.translate(lang)}</div>
                    <ClientAutocompleteInput
                        lang={lang}
                        value={scope.clientId}
                        label={scope.clientLabel}
                        onSelect={(item) => this._setScope({ clientId: item._id, clientLabel: item.username })}
                        onClear={() => this._setScope({ clientId: '', clientLabel: '' })}
                    />
                </FormGroup>

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Route'.translate(lang)}</div>
                    <Input type="text" style={{ height: 42, minWidth: 160 }} placeholder="/v2/orders" value={routeValue} onChange={(e) => this._onRouteDraft(e.target.value)} />
                </FormGroup>

                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Method'.translate(lang)}</div>
                    <Input type="select" style={{ height: 42 }} value={scope.method} onChange={(e) => this._setScope({ method: e.target.value })}>
                        <option value="">{'All'.translate(lang)}</option>
                        {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </Input>
                </FormGroup>

                {REPLICA_TABS.indexOf(tab) !== -1 ? this._renderReplicaFilter(scope) : null}

                {scope.apiKeyId ? (
                    <FormGroup style={styles.filterGroup}>
                        <div style={styles.filterLabel}>{'API key'.translate(lang)}</div>
                        <div style={styles.chip}>
                            {scope.apiKeyLabel || scope.apiKeyId}
                            <button type="button" style={styles.chipClose} onClick={() => this._setScope({ apiKeyId: '', apiKeyLabel: '' })} aria-label={'Clear'.translate(lang)}>&times;</button>
                        </div>
                    </FormGroup>
                ) : null}

                {scope.clientId || scope.route || scope.method || scope.apiKeyId || scope.replica ? (
                    <Button
                        color="secondary"
                        outline
                        style={{ height: 42 }}
                        onClick={() => {
                            this.setState({ routeDraft: null });
                            this._setScope({ clientId: '', clientLabel: '', route: '', method: '', apiKeyId: '', apiKeyLabel: '', replica: '' });
                        }}
                    >
                        {'Reset filters'.translate(lang)}
                    </Button>
                ) : null}
            </>
        );
    }

    render() {
        let lang = this.props.lang;
        let params = this._getParams();
        let tab = TABS.find((t) => t.key === params.tab) ? params.tab : 'overview';
        let period = this._period();
        let scope = this._scope();
        let hasScope = !!(scope.clientId || scope.route || scope.method || scope.apiKeyId || scope.replica);

        let detail = this._cached('detail', period, detailRange);
        let overview = this._cached('overview', period, overviewRange);
        let detailTooLong = isDetailRangeTooLong(detail);
        let overviewTooLong = !!overview && overview.days > MAX_OVERVIEW_DAYS;
        let rangeIncomplete = period.preset === 'custom' && (!period.customFrom || !period.customTo);
        let scopeProp = hasScope ? scope : {};
        let logsRange = this.state.logsRangeOverride || detail;

        return (
            <div>
                <div className="section-title title-options">
                    <h1>{'API Analytics'.translate(lang)}</h1>
                </div>

                <div style={styles.filterBar}>
                    {this._renderPeriodPicker()}
                    {this._renderScopeFilters(tab)}
                </div>

                {rangeIncomplete ? (
                    <Alert color="info">{'Pick both a from and a to date for a custom period.'.translate(lang)}</Alert>
                ) : null}

                {tab !== 'overview' && detailTooLong && !rangeIncomplete ? (
                    <Alert color="warning">
                        {`${'Detailed analysis is available for at most'.translate(lang)} ${MAX_RANGE_DAYS} ${'days. For a longer period, see the Overview tab.'.translate(lang)}`}
                    </Alert>
                ) : null}

                {tab === 'overview' && overviewTooLong ? (
                    <Alert color="warning">{`${'The range can be at most'.translate(lang)} ${MAX_OVERVIEW_DAYS} ${'days'.translate(lang)}`}</Alert>
                ) : null}

                {tab === 'overview' && hasScope ? (
                    <Alert color="info">
                        {'Overview always shows the whole API; the client/route/method/key/replica filters apply to the other tabs.'.translate(lang)}
                        {scope.clientId ? (
                            <>
                                {' '}
                                <button type="button" style={styles.link} onClick={() => this._onOpenClient({ clientId: scope.clientId, username: scope.clientLabel })}>
                                    {'Open this client\'s analytics tab'.translate(lang)}
                                </button>
                            </>
                        ) : null}
                    </Alert>
                ) : null}

                <div className="table-tabs">
                    {TABS.map((t) => (
                        <button key={t.key} className={tab === t.key ? 'active' : ''} onClick={() => this._setParams({ tab: t.key })}>
                            {t.label.translate(lang)}
                        </button>
                    ))}
                </div>

                <div style={{ marginTop: 14 }}>
                    {tab === 'overview' && overview && !overviewTooLong ? (
                        <ExternalApiOverview
                            lang={lang}
                            range={overview}
                            colorFor={this.colorFor}
                            onOpenClient={this._onOpenClient}
                        />
                    ) : null}

                    {tab === 'load' && detail && !detailTooLong ? (
                        <ExternalApiAnalytics
                            lang={lang}
                            range={detail}
                            scope={scopeProp}
                            onScopeChange={this._setScope}
                            initialFilters={{ interval: params.interval || '' }}
                            onFiltersChange={(filters) => this._setParams({ interval: filters.interval || '' })}
                            showInsights={false}
                            clientSeries={scope.clientId ? undefined : CLIENT_SERIES_COUNT}
                            colorFor={this.colorFor}
                            onOpenClient={this._onOpenClient}
                            onOpenLogs={this._onOpenLogs}
                            onReplicasLoaded={this._onReplicasLoaded}
                        />
                    ) : null}

                    {tab === 'analysis' && detail && !detailTooLong && scope.replica ? (
                        <Alert color="info">{'The analysis has no replica filter, it covers all replicas.'.translate(lang)}</Alert>
                    ) : null}

                    {tab === 'analysis' && detail && !detailTooLong ? (
                        <ExternalApiInsights
                            lang={lang}
                            clientId={scope.clientId || undefined}
                            apiKeyId={scope.apiKeyId || undefined}
                            route={scope.route || undefined}
                            method={scope.method || undefined}
                            from={detail.from}
                            to={detail.to}
                            onOpenLogs={this._onOpenLogs}
                            onOpenClient={this._onOpenClient}
                        />
                    ) : null}

                    {tab === 'logs' && detail && !detailTooLong ? (
                        <>
                            {this.state.logsRangeOverride ? (
                                <div style={{ marginBottom: 12 }}>
                                    <div style={styles.chip}>
                                        {'Period'.translate(lang)}: {moment(this.state.logsRangeOverride.from).format('YYYY-MM-DD HH:mm')} – {moment(this.state.logsRangeOverride.to).format('YYYY-MM-DD HH:mm')}
                                        <button type="button" style={styles.chipClose} onClick={() => this.setState({ logsRangeOverride: null })} aria-label={'Clear'.translate(lang)}>&times;</button>
                                    </div>
                                </div>
                            ) : null}
                            <ExternalApiLogs
                                lang={lang}
                                range={logsRange}
                                scope={scopeProp}
                                onScopeChange={this._setScope}
                                initialFilters={this.state.logsFilters}
                                resetToken={this.state.logsFilterToken}
                                onOpenClient={this._onOpenClient}
                            />
                        </>
                    ) : null}
                </div>
            </div>
        );
    }
}

export default Page(Screen);
